import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useImperativeHandle
} from 'react';
import ReactDOM from 'react-dom';
import { ControlButton } from '@components/Controls';
import useEvent from '@hooks/useEvent';
import useWindowResizing from '@hooks/useWindowResizing';
import useStyleVars from '@hooks/useStyleVars';
import useMatchMedia from '@hooks/useMatchMedia';
import useIsMounted from '@hooks/useIsMounted';
import * as styles from './Modal.module.scss';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import classNames from 'classnames';
import { isSSR } from '@helpers';
import { useSpring, animated } from 'react-spring';
import { uniqueId, isString } from 'lodash';

const initialScale = parseFloat(styles.initialScale);

export default React.memo(
  React.forwardRef(function Modal(
    {
      className,
      wrapperClassName,
      isOpen,
      contentIsScrollable = true,
      header,
      headerLabelId,
      headerClassName,
      headerChildren,
      closeLabel,
      closeColor,
      closeClassName,
      contentClassName,
      children,
      hideWithCss = true,
      style = {},
      buttonText,
      buttonClassName,
      buttonIsDisabled,
      buttonAriaControls = null,
      onButtonClick,
      setIsOpen,
      close,
      ...restProps
    },
    ref
  ) {
    //////////////////////////
    // State
    //////////////////////////

    const wrapperRef = useRef(null);
    const contentRef = useRef(null);
    const scrollWrapperRef = useRef(null);

    const closeModal = useCallback(() => {
      if (setIsOpen) {
        setIsOpen(false);
      }

      if (close) {
        close();
      }
    }, [close, setIsOpen]);

    useEvent('keydown', e => {
      if (isOpen && e.key === 'Escape') {
        closeModal();
      }
    });

    const { modalMobileWidth } = useStyleVars();
    const isMobile = useMatchMedia(modalMobileWidth);
    const windowIsResizing = useWindowResizing(250);
    const isMounted = useIsMounted();

    const isResizing = useRef(windowIsResizing);

    useEffect(() => {
      isResizing.current = windowIsResizing;
    }, [windowIsResizing]);

    //////////////////////////
    // Animation
    //////////////////////////

    const [contentIsAnimating, _setContentIsAnimating] = useState(false);
    const isHidden = useRef(true);

    useEffect(() => {
      if (isOpen) {
        isHidden.current = false;
      }
    }, [isOpen]);

    const setContentIsAnimating = useCallback(
      isAnimating => {
        if (!isMounted()) {
          return;
        }

        _setContentIsAnimating(isAnimating);
      },
      [isMounted]
    );

    const isVisible = useMemo(() => {
      return isOpen || !isHidden.current;
    }, [isOpen]);

    const contentStyle = useMemo(() => {
      return !isMobile
        ? {
            opacity: isOpen ? 1 : 0,
            scale: isOpen ? 1 : initialScale,
            pause: !isVisible,
            delay: isOpen ? 150 : 0,
            config: {
              mass: null,
              tension: 400,
              friction: null,
              precision: null,
              duration: isOpen ? undefined : 0
            }
          }
        : {
            y: isOpen ? '0%' : '100%',
            pause: !isVisible,
            config: {
              mass: 0.1,
              tension: 280,
              friction: 30,
              precision: 0.0001,
              velocity: 0.015
            }
          };
    }, [isMobile, isOpen, isVisible]);

    const contentSpring = useSpring({
      ...contentStyle,
      immediate: () => isResizing.current || !isVisible,
      onStart: () => {
        if (!isResizing.current) {
          setContentIsAnimating(true);
        }
      },
      onRest: () => {
        setContentIsAnimating(false);

        if (!isOpen) {
          isHidden.current = true;
          wrapperRef.current?.classList.add('modal-hidden');
        }
      }
    });

    const springRef = useRef(contentSpring);

    useEffect(() => {
      springRef.current = contentSpring;
    }, [contentSpring]);

    useEffect(() => {
      if (isMobile) {
        springRef.current.scale?.set(1);
      } else {
        springRef.current.y?.set(1);
      }
    }, [isMobile]);

    //////////////////////////
    // Header
    //////////////////////////

    useEffect(() => {
      if (isOpen) {
        setTimeout(() => {
          wrapperRef.current?.scrollTo(0, 0);
        }, 0);

        const el = contentRef.current;

        if (el) {
          el.tabIndex = 0;
          el.focus();
          el.blur();
          el.removeAttribute('tabindex');
        }
      }
    }, [isOpen]);

    let headerComponent = null;
    let hasAriaLabel = !!headerLabelId;

    const labelId = useMemo(() => {
      return headerLabelId ?? uniqueId('modal-label-');
    }, [headerLabelId]);

    if (React.isValidElement(header)) {
      headerComponent = header;
    } else {
      const hasLabel = isString(header);
      hasAriaLabel = hasLabel;

      const closeButton = (
        <CloseModalButton
          className={closeClassName}
          color={closeColor}
          label={closeLabel}
          close={closeModal}
        />
      );

      headerComponent =
        hasLabel || headerChildren ? (
          <div className={classNames(styles.header, headerClassName)}>
            {hasLabel && <h2 id={labelId}>{header}</h2>}
            {closeButton}
            {headerChildren}
          </div>
        ) : (
          closeButton
        );
    }

    const headerWrapper = useRef();

    if (headerComponent) {
      headerComponent = (
        <div ref={headerWrapper} className={styles.headerWrapper}>
          {headerComponent}
        </div>
      );
    }

    useImperativeHandle(ref, () => ({
      getContentRef: () => contentRef,
      getScrollWrapperRef: () => scrollWrapperRef,
      resetScroll: () => {
        scrollWrapperRef.current?.scrollTo(0, 0);
      },
      getOffsetLeft: () => contentRef.current?.offsetLeft ?? null
    }));

    return isSSR() || (!isOpen && !hideWithCss)
      ? null
      : ReactDOM.createPortal(
          <FocusLock autoFocus={false} disabled={!isOpen}>
            <RemoveScroll
              allowPinchZoom
              ref={wrapperRef}
              enabled={isOpen}
              className={classNames(styles.wrapper, wrapperClassName, {
                open: isOpen,
                closed: !isOpen,
                animating: contentIsAnimating && !isResizing.current,
                'modal-hidden': !isVisible
              })}
              onMouseDown={e => {
                if (e.target === wrapperRef.current) {
                  closeModal();
                }
              }}
            >
              <animated.div
                ref={contentRef}
                role="dialog"
                aria-modal="true"
                aria-hidden={!isOpen}
                aria-labelledby={hasAriaLabel ? labelId : null}
                className={classNames(
                  'calculator-modal-content',
                  styles.contentWrapper,
                  className
                )}
                style={{ ...contentSpring, ...style }}
                {...restProps}
              >
                <div className={classNames(styles.content, contentClassName)}>
                  {headerComponent}
                  {!contentIsScrollable ? (
                    children
                  ) : (
                    <div
                      ref={scrollWrapperRef}
                      className={styles.scrollWrapper}
                    >
                      <div className={styles.innerContent}>{children}</div>
                      {!buttonText ? null : (
                        <div className={styles.buttonWrapper}>
                          <ControlButton
                            small
                            className={classNames(
                              styles.button,
                              buttonClassName
                            )}
                            aria-controls={buttonAriaControls}
                            disabled={!!buttonIsDisabled}
                            onClick={onButtonClick}
                          >
                            {buttonText}
                          </ControlButton>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </animated.div>
              <div className={styles.bg} />
            </RemoveScroll>
          </FocusLock>,
          document.body
        );
  })
);

export const CloseModalButton = React.forwardRef(
  ({ className, label = 'Close', close, color }, ref) => {
    return (
      <button
        ref={ref}
        className={classNames(
          'calculator-close-modal',
          styles.close,
          className
        )}
        aria-label={label}
        style={{ color }}
        onClick={e => {
          e.stopPropagation();
          close();
        }}
      >
        <span />
      </button>
    );
  }
);
